/**
 * Public Safety QR scan page — anyone scanning a QR sees asset info,
 * captcha-gated alert send with optional location, and WhatsApp deep link.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, AlertTriangle, MapPin, Send, CheckCircle2, Flag } from 'lucide-react';
import api from '../../utils/api';

export default function SafetyQRScan() {
  const { qrId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [loc, setLoc] = useState(null);
  const [captchaAns, setCaptchaAns] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.get(`/safety-qr/scan/${qrId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'QR not found or revoked.'));
  }, [qrId]);

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error('Location supported nahi is browser mein');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success('📍 Location captured'); },
      () => toast.error('Location permission denied')
    );
  };

  const sendAlert = async () => {
    if (message.trim().length < 3) return toast.error('Message likhein');
    if (!captchaAns) return toast.error('Captcha ka jawab dein');
    setBusy(true);
    try {
      const r = await api.post(`/safety-qr/scan/${qrId}/alert`, {
        message,
        lat: loc?.lat, lng: loc?.lng,
        captcha_answer: Number(captchaAns),
        captcha_key: data.captcha.key,
      });
      setSent(r.data);
      toast.success('Alert bhej diya!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      // refresh ONLY captcha (not full scan) — fixes "captcha 2-3 baar" UX bug
      try {
        const c = await api.get(`/safety-qr/scan/${qrId}/captcha`);
        setData((prev) => (prev ? { ...prev, captcha: c.data } : prev));
        setCaptchaAns('');
      } catch (_) { /* silent */ }
    } finally { setBusy(false); }
  };

  const reportSpam = async () => {
    if (!window.confirm('Report this QR as spam/misuse?')) return;
    await api.post(`/safety-qr/scan/${qrId}/report-spam`, { reason: 'User reported' });
    toast.success('Reported. Thank you.');
  };

  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-4">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-2" />
        <div className="font-bold">{error}</div>
      </div>
    </div>;
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-white">Loading…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/50 to-slate-950 text-white p-4" data-testid="safety-scan-public">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center py-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 items-center justify-center mb-2">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div className="text-red-400 text-xs font-bold uppercase tracking-widest">Anant Safety QR</div>
          <h1 className="text-xl font-extrabold mt-1">Emergency Response</h1>
        </div>

        {/* Emergency Headline Banner */}
        {data.emergency_headline && (
          <div className="rounded-xl bg-gradient-to-r from-red-600 to-orange-600 text-white p-3 mb-3 text-center font-extrabold text-lg shadow-lg animate-pulse" data-testid="scan-emerg-headline">
            {data.emergency_headline}
          </div>
        )}

        {/* Default India Emergency Numbers — always visible */}
        {(data.emergency_numbers || []).length > 0 && (
          <div className="rounded-2xl border border-red-400/40 bg-slate-900/70 p-3 mb-3" data-testid="scan-emerg-numbers">
            <div className="text-red-300 text-[11px] font-bold uppercase mb-2 text-center">🆘 Emergency Numbers — 1-tap to call</div>
            <div className="grid grid-cols-3 gap-1.5">
              {data.emergency_numbers.map((n, i) => (
                <a key={i} href={`tel:${n.number}`} data-testid={`emerg-num-${i}`}
                  className="flex flex-col items-center bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 rounded-lg px-1.5 py-2 text-center transition">
                  <div className="text-lg leading-none">{n.icon || '📞'}</div>
                  <div className="text-[10px] font-bold text-red-100 mt-0.5">{n.label}</div>
                  <div className="text-[11px] font-mono font-extrabold text-white">{n.number}</div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Asset card */}
        <div className="rounded-2xl border border-red-400/30 bg-slate-900/70 p-4 mb-3">
          <div className="text-center">
            <div className="text-5xl mb-2">{data.asset_icon}</div>
            <div className="text-[11px] uppercase tracking-widest text-red-300">{data.asset_type_label}</div>
            <div className="text-xl font-bold mt-1">{data.asset_label}</div>
            {data.custom_message && <div className="text-sm text-slate-300 mt-2 italic">"{data.custom_message}"</div>}
            {data.additional_info && <div className="text-xs text-slate-400 mt-2" data-testid="scan-additional">{data.additional_info}</div>}
          </div>
          <div className="border-t border-white/10 mt-3 pt-3 text-sm space-y-1">
            <div className="text-slate-400 text-xs">Owner:</div>
            <div className="font-bold">{data.owner_name}</div>
            <div className="text-xs text-slate-400">📞 {data.owner_whatsapp_masked}</div>
          </div>
          {data.medical_info && (
            <div className="border-t border-white/10 mt-3 pt-3">
              <div className="text-red-300 text-xs font-bold flex items-center gap-1 mb-1"><AlertTriangle className="w-3 h-3" /> MEDICAL INFO</div>
              <div className="text-sm text-slate-200">{data.medical_info}</div>
            </div>
          )}
        </div>

        {sent ? (
          <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-center" data-testid="safety-scan-sent">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-2" />
            <div className="font-bold text-emerald-200">Alert bhej diya! Owner ko notification mil chuki.</div>
            <a href={sent.whatsapp_link} target="_blank" rel="noreferrer" className="inline-block mt-3 px-4 py-2 rounded-xl bg-emerald-500 text-white font-bold text-sm">📱 WhatsApp par bhi bhejein</a>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 space-y-3" data-testid="safety-scan-form">
            <div className="text-sm font-bold">Alert send karein</div>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="safety-msg" placeholder="Kya hua hai? e.g. 'Aapki gaadi galat parking mein hai, hataayein'" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <button onClick={captureLocation} data-testid="safety-loc-btn" className={`w-full py-2 rounded-lg border text-xs font-bold flex items-center justify-center gap-1 ${loc ? 'border-emerald-400/40 bg-emerald-500/20 text-emerald-200' : 'border-white/10 bg-white/5 text-slate-300'}`}>
              <MapPin className="w-3 h-3" /> {loc ? `Location added ✓` : 'Share my location (recommended)'}
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono bg-white/5 px-2 py-1 rounded">{data.captcha.question}</span>
              <input type="number" value={captchaAns} onChange={(e) => setCaptchaAns(e.target.value)} data-testid="safety-captcha" placeholder="Answer" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-sm" />
            </div>
            <button onClick={sendAlert} disabled={busy} data-testid="safety-send-btn" className="w-full py-3 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-1">
              <Send className="w-4 h-4" /> {busy ? 'Bhej rahe…' : 'Send Alert'}
            </button>
            <button onClick={reportSpam} className="w-full text-[11px] text-slate-500 hover:text-red-300 flex items-center justify-center gap-1"><Flag className="w-3 h-3" /> Report Spam</button>
          </div>
        )}

        <div className="text-center text-[10px] text-slate-500 mt-4">Powered by Anant Web Portal · Anti-abuse enabled</div>
      </div>
    </div>
  );
}
